import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "../data/content";
import BrandGlyph from "./BrandGlyph";
import { motion, useReducedMotion, useScroll } from "framer-motion";

const Logo = () => (
  <Link
    to="/"
    aria-label="Go to Thrivehaus home page"
    className="flex items-center gap-2"
  >
    <span className="font-display text-lg font-semibold tracking-tight text-[#f5efe6] sm:text-xl">
      THRIVEHAUS
    </span>
    <span className="font-mono text-[10px] font-semibold tracking-[.18em] text-[#d0a26b]">
      DIGITAL
    </span>
    <BrandGlyph className="h-5 w-6" />
  </Link>
);

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const update = () => setScrolled(window.scrollY > 18);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const trigger = useRef(null);
  const dialog = useRef(null);
  const location = useLocation();
  useEffect(() => setOpen(false), [location.pathname, location.search]);
  useEffect(() => {
    if (!open) return;
    const triggerElement = trigger.current;
    const background = [...document.querySelectorAll("[data-menu-background]")];
    const previous = background.map((element) => element.inert);
    background.forEach((element) => {
      element.inert = true;
    });
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusables = () => [
      ...dialog.current.querySelectorAll("a[href],button:not([disabled])"),
    ];
    focusables()[0]?.focus();
    const keydown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
      }
      if (event.key !== "Tab") return;
      const items = focusables();
      const first = items[0],
        last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const focusin = (event) => {
      if (!dialog.current?.contains(event.target)) focusables()[0]?.focus();
    };
    const wide = window.matchMedia("(min-width: 1024px)");
    const resize = () => {
      if (wide.matches) setOpen(false);
    };
    document.addEventListener("keydown", keydown);
    document.addEventListener("focusin", focusin);
    wide.addEventListener("change", resize);
    return () => {
      background.forEach((element, index) => {
        element.inert = previous[index];
      });
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", keydown);
      document.removeEventListener("focusin", focusin);
      wide.removeEventListener("change", resize);
      triggerElement?.focus({ preventScroll: true });
    };
  }, [open]);

  return (
    <>
      <header
        data-menu-background
        className={`fixed inset-x-0 top-0 z-[70] border-b border-white/10 bg-[#0b0a08]/95 text-white shadow-[0_14px_44px_-30px_rgba(0,0,0,.95)] backdrop-blur-xl transition-colors duration-500 ${scrolled ? "sm:bg-[#0b0a08]/92" : "sm:bg-[#0b0a08]/82"}`}
      >
        <div className="site-container flex h-16 items-center justify-between sm:h-20">
          <Logo />
          <nav
            className="hidden items-center gap-7 lg:flex"
            aria-label="Primary"
          >
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.path}
                to={link.path}
                end={link.path === "/services" && location.pathname.startsWith("/services/website-packages")}
                className={({ isActive }) =>
                  `font-mono text-xs font-semibold uppercase tracking-[.1em] ${isActive ? "text-[#d0a26b]" : "text-white/80 hover:text-white"}`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact?source=nav"
              className="inline-flex min-h-11 items-center gap-2 bg-[#d0a26b] px-5 py-3 font-mono text-xs font-bold uppercase tracking-[.1em] text-[#15110c]"
            >
              Start a Project <ArrowUpRight className="h-4 w-4" />
            </Link>
          </nav>
          <button
            ref={trigger}
            type="button"
            aria-label="Open menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen(true)}
            className="grid h-11 w-11 place-items-center rounded-full border border-[#d0a26b]/50 text-[#d0a26b] lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
        <motion.div aria-hidden="true" className="absolute bottom-0 left-0 h-px w-full origin-left bg-[#d0a26b]" style={{scaleX: scrollYProgress}} />
      </header>
      {open && (
        <motion.div
          initial={reduce ? false : { opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          ref={dialog}
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
          className="fixed inset-0 z-[80] overflow-y-auto bg-[#0b0a08] text-white lg:hidden"
        >
          <div className="site-container flex min-h-full flex-col pb-6">
            <div className="flex h-16 shrink-0 items-center justify-between sm:h-20">
              <span className="font-display text-lg text-[#f5efe6]">
                THRIVEHAUS DIGITAL
              </span>
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="grid h-11 w-11 place-items-center rounded-full border border-[#d0a26b]/50 text-[#d0a26b]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav aria-label="Mobile" className="mt-4">
              {NAV_LINKS.map((link, i) => (
                <motion.div key={link.path} initial={reduce ? false : {opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{delay:.05+i*.045,duration:.45}}>
                <NavLink
                  end={link.path === "/" || (link.path === "/services" && location.pathname.startsWith("/services/website-packages"))}
                  key={link.path}
                  to={link.path}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex min-h-14 items-center border-b border-white/15 py-3 font-display text-3xl ${isActive ? "text-[#d0a26b]" : "text-[#f5efe6]"}`
                  }
                >
                  {link.label}
                </NavLink>
                </motion.div>
              ))}
            </nav>
            <div className="mt-auto pt-8">
              <Link
                to="/contact?source=mobile-nav"
                onClick={() => setOpen(false)}
                className="flex min-h-12 items-center justify-center gap-3 bg-[#d0a26b] px-5 py-4 font-mono text-xs font-bold uppercase tracking-[.1em] text-[#15110c]"
              >
                Start a Project <ArrowUpRight className="h-4 w-4" />
              </Link>
              <p className="mt-4 text-center text-xs text-white/75">
                Adelaide · Australia + worldwide
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </>
  );
}
